import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  LabelList
} from "recharts";
import styles from "./css/BarsGraph.module.css";
import { LoadingWaves } from '../addons/LoadingWaves';

interface ChargingData {
  [year: string]: {
    "כמות עמדות AC ציבוריות נדרשת": number;
    "כמות עמדות DC ציבוריות נדרשת": number;
  };
}

interface ChartDataItem {
  year: string;
  AC: number;
  DC: number;
  ACLabel: string;
  DCLabel: string;
}

const COLORS: { [key: string]: string } = {
  AC: "#4A90E2",
  DC: "#50C878"
};

interface ChargingBarsGraphCityProps {
  data: ChargingData | any;
  size?: 'small' | 'normal';
}

export const ChargingBarsGraphCity: React.FC<ChargingBarsGraphCityProps> = ({ data, size = 'normal' }) => {
  const formatNumber = (value: number) => {
    if (typeof value !== 'number') return value;
    return value.toLocaleString();
  };

  // Dynamically extract years from data
  const years = data ? Object.keys(data).sort() : [];
  const firstYear = years[0] || "2025";
  const secondYear = years[1] || "2040";

  const transformedData: ChartDataItem[] = data ? [
    {
      year: firstYear,
      AC: data[firstYear]?.["כמות עמדות AC ציבוריות נדרשת"] || 0,
      DC: data[firstYear]?.["כמות עמדות DC ציבוריות נדרשת"] || 0,
      ACLabel: "AC",
      DCLabel: "DC"
    },
    {
      year: secondYear,
      AC: data[secondYear]?.["כמות עמדות AC ציבוריות נדרשת"] || 0,
      DC: data[secondYear]?.["כמות עמדות DC ציבוריות נדרשת"] || 0,
      ACLabel: "AC",
      DCLabel: "DC"
    }
  ] : [];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white p-2 border border-gray-200 rounded shadow-sm">
        <p className="text-sm font-medium mb-1">{label}</p>
        {payload.map((item: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.fill }} />
            <span>{item.name}:</span>
            <span className="font-medium">{formatNumber(item.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  if (!data) {
    return (
      <div className={styles.container}>
        <LoadingWaves color={Object.values(COLORS)[0]} />
      </div>
    );
  }

  const maxValue = Math.max(...transformedData.map(item => Math.max(item.AC, item.DC)));
  const displayMaxValue = Math.ceil(maxValue * 1.1);
  const fontSize = size === 'small' ? 10 : 12;

  return (
    <div className={styles.container}>
      <div className="w-full h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={transformedData}
            margin={{ top: 20, right: 20, left: -40, bottom: 0 }}
            barGap={4}
            barCategoryGap={25}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E5E5" />
            <XAxis
              dataKey="year"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6C7693', fontSize }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6C7693', fontSize: fontSize - 1 }}
              tickFormatter={formatNumber}
              domain={[0, displayMaxValue]}
            />
            <Tooltip content={CustomTooltip} />
            <Bar dataKey="AC" name="כמות עמדות AC" fill={COLORS["AC"]} radius={[3, 3, 0, 0]} barSize={28}>
              <LabelList dataKey="ACLabel" position="top" fill="#6C7693" fontSize={fontSize} offset={5} />
            </Bar>
            <Bar dataKey="DC" name="כמות עמדות DC" fill={COLORS["DC"]} radius={[3, 3, 0, 0]} barSize={28}>
              <LabelList dataKey="DCLabel" position="top" fill="#6C7693" fontSize={fontSize} offset={5} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
